import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { popPendingLeagueCode } from "../pages/JoinLeague";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchMe = useCallback(async () => {
    const token = localStorage.getItem("fairway_token");
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      localStorage.removeItem("fairway_token");
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchMe(); }, [fetchMe]);

  const consumePendingInvite = async () => {
    const code = popPendingLeagueCode();
    if (!code) return null;
    try {
      const { data } = await api.post("/leagues/join", { code });
      return data.id;
    } catch {
      return null;
    }
  };

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("fairway_token", data.token);
    setUser(data.user);
    const leagueId = await consumePendingInvite();
    return { user: data.user, joinedLeagueId: leagueId };
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    localStorage.setItem("fairway_token", data.token);
    setUser(data.user);
    const leagueId = await consumePendingInvite();
    return { user: data.user, joinedLeagueId: leagueId };
  };

  const logout = () => {
    localStorage.removeItem("fairway_token");
    setUser(null);
  };

  const toggleFavourite = async (courseId) => {
    if (!user) return;
    const isFav = (user.favourite_course_ids || []).includes(courseId);
    // Optimistic update
    setUser((u) => {
      const cur = u.favourite_course_ids || [];
      return {
        ...u,
        favourite_course_ids: isFav ? cur.filter((id) => id !== courseId) : [...cur, courseId],
      };
    });
    try {
      const { data } = isFav
        ? await api.delete(`/auth/me/favourites/${courseId}`)
        : await api.post(`/auth/me/favourites/${courseId}`);
      setUser(data);
    } catch {
      // Roll back on failure
      fetchMe();
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refresh: fetchMe, toggleFavourite }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
